/**
 * ShareDrawer - Slide-out panel for journal sharing
 * 
 * Modernized UI with:
 * - Large hero cover image at top
 * - Inline editable description and tags
 * - Toggle at bottom (action after preview)
 * - Smooth animations
 */

import { useState, useEffect } from "react";
import { useShareJournal, useUpdateShareMetadata } from "@/hooks/useShareJournal";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Globe,
    X,
    Plus,
    Loader2,
    Eye,
    Copy,
    Sparkles,
    ExternalLink,
} from "lucide-react";

interface ShareDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    journalId: string;
    journalName: string;
    journalSummary?: string;
    currentSharingMode: 'private' | 'community' | null;
    currentTags?: string[];
    currentDescription?: string;
    currentCoverUrl?: string;
    viewCount?: number;
    copyCount?: number;
}

export const ShareDrawer = ({
    open,
    onOpenChange,
    journalId,
    journalName,
    journalSummary,
    currentSharingMode,
    currentTags = [],
    currentDescription = '',
    currentCoverUrl = '',
    viewCount = 0,
    copyCount = 0,
}: ShareDrawerProps) => {
    const shareMutation = useShareJournal();
    const updateMetadataMutation = useUpdateShareMetadata();

    const [isShared, setIsShared] = useState(currentSharingMode === 'community');
    const [tags, setTags] = useState<string[]>(currentTags);
    const [description, setDescription] = useState(currentDescription);
    const [newTag, setNewTag] = useState('');
    const [isEditingDescription, setIsEditingDescription] = useState(false);

    // Sync local state with props
    useEffect(() => {
        setIsShared(currentSharingMode === 'community');
        setTags(currentTags);
        setDescription(currentDescription);
    }, [currentSharingMode, currentTags, currentDescription]);

    const handleToggleShare = async () => {
        const newSharedState = !isShared;
        setIsShared(newSharedState);

        await shareMutation.mutateAsync({
            journalId,
            sharingMode: newSharedState ? 'community' : null,
            journalName,
            journalSummary,
        });
    };

    const handleAddTag = () => {
        if (newTag.trim() && !tags.includes(newTag.trim())) {
            const updatedTags = [...tags, newTag.trim()];
            setTags(updatedTags);
            setNewTag('');

            if (isShared) {
                updateMetadataMutation.mutate({ journalId, tags: updatedTags });
            }
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        const updatedTags = tags.filter(t => t !== tagToRemove);
        setTags(updatedTags);

        if (isShared) {
            updateMetadataMutation.mutate({ journalId, tags: updatedTags });
        }
    };

    const handleDescriptionSave = () => {
        setIsEditingDescription(false);
        if (isShared && description !== currentDescription) {
            updateMetadataMutation.mutate({ journalId, description });
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-[420px] sm:max-w-[420px] p-0 overflow-y-auto"
            >
                {/* Header */}
                <SheetHeader className="px-6 py-4 border-b border-gray-100">
                    <SheetTitle className="flex items-center gap-2 text-base font-medium">
                        <Globe className="w-4 h-4 text-gray-500" />
                        Share Journal
                        {isShared && (
                            <Badge className="bg-emerald-50 text-emerald-600 border-0 text-xs font-normal">
                                Live
                            </Badge>
                        )}
                    </SheetTitle>
                </SheetHeader>

                <div className="px-6 py-5 space-y-6">
                    {/* Cover Image - Hero */}
                    <div className="space-y-2">
                        {currentCoverUrl ? (
                            <div className="relative group">
                                <img
                                    src={currentCoverUrl}
                                    alt="Cover"
                                    className="w-full h-48 object-cover rounded-xl transition-transform duration-300"
                                />
                                <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-md text-white text-xs">
                                    <Sparkles className="w-3 h-3" />
                                    AI generated
                                </div>
                            </div>
                        ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl flex items-center justify-center">
                                <div className="text-center text-slate-400">
                                    <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Cover image will be generated when shared</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Title</p>
                        <h2 className="text-lg font-medium text-slate-900">{journalName}</h2>
                    </div>

                    {/* Description - Inline Edit */}
                    <div className="space-y-2">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Description</p>
                        {isEditingDescription ? (
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                onBlur={handleDescriptionSave}
                                autoFocus
                                placeholder="Brief description for the community..."
                                rows={3}
                                className="resize-none text-sm border-slate-200 focus:border-slate-300"
                            />
                        ) : (
                            <div
                                onClick={() => setIsEditingDescription(true)}
                                className="text-sm text-slate-600 cursor-pointer hover:bg-slate-50 p-2 -m-2 rounded-lg transition-colors min-h-[60px]"
                            >
                                {description || (
                                    <span className="text-slate-400 italic">
                                        Click to add description...
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Topics/Tags */}
                    <div className="space-y-2">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Topics</p>
                        <div className="flex flex-wrap gap-2">
                            {tags.map((tag, idx) => (
                                <Badge
                                    key={idx}
                                    variant="secondary"
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 gap-1.5 pl-3 pr-2 py-1 transition-colors"
                                >
                                    {tag}
                                    <button
                                        onClick={() => handleRemoveTag(tag)}
                                        className="hover:text-red-500 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </Badge>
                            ))}
                            <div className="flex items-center">
                                <Input
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                    placeholder="Add topic..."
                                    className="h-7 w-28 text-sm border-dashed border-slate-300"
                                />
                                <button
                                    onClick={handleAddTag}
                                    className="p-1.5 hover:bg-slate-100 rounded-md transition-colors ml-1"
                                >
                                    <Plus className="w-4 h-4 text-slate-400" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-slate-100" />

                    {/* Share Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-slate-900">Share to Community</p>
                            <p className="text-sm text-slate-500">
                                Visible on the Explore page
                            </p>
                        </div>
                        <Switch
                            checked={isShared}
                            onCheckedChange={handleToggleShare}
                            disabled={shareMutation.isPending}
                            className="data-[state=checked]:bg-emerald-500"
                        />
                    </div>

                    {/* Loading State */}
                    {shareMutation.isPending && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Generating cover and metadata...</span>
                        </div>
                    )}

                    {/* Stats & Link - Only when shared */}
                    {isShared && !shareMutation.isPending && (
                        <>
                            <div className="border-t border-slate-100" />

                            {/* Stats Row */}
                            <div className="flex items-center justify-between text-sm text-slate-500">
                                <div className="flex items-center gap-4">
                                    <span className="flex items-center gap-1.5">
                                        <Eye className="w-4 h-4" />
                                        {viewCount} views
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Copy className="w-4 h-4" />
                                        {copyCount} copies
                                    </span>
                                </div>
                            </div>

                            {/* View on Explore Link */}
                            <a
                                href={`/community/journal/${journalId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                View on Explore
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default ShareDrawer;
